var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import useFieldApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import { INPUT_LABEL, INPUT, INPUT_GROUP, INPUT_DESCRIPTION, INPUT_GROUP_POST_FIX, IS_INVALID, FLEX_NOWRAP, DATE_PICKER_PORTAL, INPUT_RECOMMENDED, INPUT_RECOMMENDED_TITLE, INPUT_RECOMMENDED_INNER, TEXT_INPUT } from "../../../classConstants";
import { Flex } from '../../Flex';
import styled from 'styled-components';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { ErrorTypes, ErrorText } from '../ErrorMessages';
import useFormApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-form-api';
import parse from 'html-react-parser';
import { getClassName } from '../../../helpers';
import { usePresentationComponent } from '../../../AppContext';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '../../../icons';
import { DatePicker } from "./DatePicker";
import { DateField } from "./DateField";
import { TimeField } from "./TimeField";
var InputNoStyle = function (props) {
    var _a;
    var t = useTranslation().t;
    var recommended = props.recommended, _b = props.type, type = _b === void 0 ? 'text' : _b, _c = props.className, className = _c === void 0 ? "" : _c, name = props.name, label = props.label, required = props.required, checked = props.checked, _d = props.children, children = _d === void 0 ? null : _d, defaultChecked = props.defaultChecked, _e = props.isInline, isInline = _e === void 0 ? false : _e, error_message = props.error_message, _f = props.input, _g = _f === void 0 ? { name: '' } : _f, _h = _g.onBlur, onBlur = _h === void 0 ? null : _h, _j = _g.onFocus, onFocus = _j === void 0 ? null : _j, input = __rest(_g, ["onBlur", "onFocus"]), _k = props.meta, _l = _k === void 0 ? {} : _k, _m = _l.touched, touched = _m === void 0 ? false : _m, _o = _l.invalid, invalid = _o === void 0 ? false : _o, _p = _l.error, error = _p === void 0 ? '' : _p, disableCopyPaste = props.disableCopyPaste, leftIcon = props.leftIcon, rightIcon = props.rightIcon, inputClassName = props.inputClassName, inputProps = __rest(props, ["recommended", "type", "className", "name", "label", "required", "checked", "children", "defaultChecked", "isInline", "error_message", "input", "meta", "disableCopyPaste", "leftIcon", "rightIcon", "inputClassName"]);
    var preventAutocomplete = React.useMemo(function () { return name + Math.random(); }, []);
    var isRadioOrCheckbox = ["checkbox", "radio"].includes(type);
    var displayInline = isRadioOrCheckbox || isInline;
    var childrenComponent = typeof children === "string" ? parse(children) : children;
    var typeClassName = "ws-" + type;
    var showDescription = !!(label && children);
    var isInvalid = error_message || (touched && error);
    var datePickerPortalId = React.useMemo(function () { return "ws-date-picker-wrapper" + name.replace(".", "_"); }, []);
    var forwardedAs = type === "birthday" ? "div" : "label";
    var renderAsRecommended = type === "checkbox";
    /**
     *
     * Get custom renderer from the presentation components in the context
     *
     */
    var componentRendererMap = {
        checkbox: usePresentationComponent("checkbox"),
        radio: usePresentationComponent("radio"),
        text: usePresentationComponent("text"),
        time: usePresentationComponent("time"),
        date: usePresentationComponent("date"),
        birthday: usePresentationComponent("date"),
        "date-picker": usePresentationComponent("date")
    };
    var getComponentRenderer = function (_a, type) {
        var useDefaultRenderer = _a.useDefaultRenderer, portalId = _a.portalId, props = __rest(_a, ["useDefaultRenderer", "portalId"]);
        if (useDefaultRenderer)
            return null;
        var Component = componentRendererMap[type] || componentRendererMap["text"];
        if (!Component)
            return null;
        var componentProps = __assign(__assign({}, props), { required: required, label: getLabel(), trans: t, okLabelKey: "common.ok", cancelLabelKey: "common.close", error: getError() });
        // @ts-ignore
        return React.createElement(Component, __assign({}, componentProps));
    };
    /**
     *
     * @returns string | JSX.Element | JSX.element[]
     * The display label of the input field
     *
     */
    var getLabel = function () { return typeof label === "string" ? parse(label) : label; };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * Display the error component.
     *
     */
    var getError = function () {
        var Error = isInvalid ? (ErrorTypes[error] || ErrorText) : null;
        return Error && React.createElement(Error, { label: label, className: className, error: error_message || error });
    };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * display either error or the label but never both
     *
     */
    var renderDisplayText = function () {
        var className = getClassName(INPUT_LABEL, typeClassName + "-label");
        var parsedLabel = getLabel();
        var renderLabel = !isEmpty(parsedLabel);
        return React.createElement(React.Fragment, null, isInvalid
            ? getError()
            : React.createElement("span", { className: className },
                renderLabel ? parsedLabel : childrenComponent,
                renderLabel && required && React.createElement("span", null, "*")));
    };
    /**
     *
     * @returns JSX.Element | JSX.element[] | string
     * display the description of field.
     *
     */
    var renderDescription = function () {
        return showDescription ? React.createElement(Flex, { className: INPUT_DESCRIPTION, isInline: displayInline },
            React.createElement("div", null,
                " ",
                childrenComponent,
                " ")) : null;
    };
    var renderDateFieldPortal = function () {
        if (type !== "date" && type !== "date-picker")
            return null;
        return React.createElement(DatePortal, { id: datePickerPortalId, className: DATE_PICKER_PORTAL });
    };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * Display the checkbox component.
     * If a custom renderer is provided in the context, use that
     *
     */
    var renderCheckboxRadio = function () {
        var isChecked = !!checked || !!input.checked;
        var props = __assign(__assign(__assign({}, inputProps), input), { defaultChecked: input.onChange ? undefined : defaultChecked, checked: isChecked, name: name || input.name });
        var alternateComponent = getComponentRenderer(props, type);
        return React.createElement(React.Fragment, null,
            alternateComponent || React.createElement("input", __assign({ className: typeClassName }, props, { type: type })),
            (!alternateComponent || !label) && renderDisplayText());
    };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * Display the input component.
     * If a custom renderer is provided in the context, use that
     *
     */
    var renderInput = function (additionaProps) {
        if (additionaProps === void 0) { additionaProps = {}; }
        var props = __assign(__assign(__assign(__assign({}, inputProps), input), additionaProps), { defaultValue: input.onChange ? undefined : input.defaultValue, autoComplete: disableCopyPaste ? preventAutocomplete : undefined, onPaste: disableCopyPaste ? function (e) {
                e.preventDefault();
                return false;
            } : null });
        var alternateComponent = getComponentRenderer(props, type);
        return React.createElement(React.Fragment, null,
            !alternateComponent && renderDisplayText(),
            React.createElement(FieldWrap, null,
                leftIcon,
                alternateComponent || React.createElement("input", __assign({ className: getClassName(typeClassName, TEXT_INPUT, inputClassName || INPUT) }, props, { type: type })),
                !alternateComponent && isInvalid && React.createElement(Icon, { type: "error" }),
                rightIcon));
    };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * Display the date-picker component.
     * If a custom renderer is provided in the context, use that
     *
     */
    var renderDatePicker = function () {
        var dateFormat = t("date.date_input_format");
        var dateProps = __assign(__assign(__assign({ dateFormat: dateFormat }, inputProps), input), { name: input.name, defaultValue: input.onChange ? undefined : input.defaultValue, autoComplete: preventAutocomplete, locale: t("locale"), includeDates: get(props, "includeDates"), portalId: datePickerPortalId });
        var alternateComponent = getComponentRenderer(dateProps, type);
        return React.createElement(React.Fragment, null,
            !alternateComponent && renderDisplayText(),
            React.createElement(FieldWrap, null,
                alternateComponent || React.createElement(DatePicker, __assign({}, dateProps)),
                !alternateComponent && !isInvalid && React.createElement(Icon, { style: { cursor: "pointer" }, type: "calendar" }),
                !alternateComponent && isInvalid && React.createElement(Icon, { type: "error" })));
    };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * Display the date field component.
     * If a custom renderer is provided in the context, use that
     *
     */
    var renderBirthday = function () {
        var dateFormat = t("date.date_input_format");
        var props = __assign(__assign(__assign({ dateFormat: dateFormat }, inputProps), input), { name: input.name, defaultValue: input.onChange ? undefined : input.defaultValue, autoComplete: preventAutocomplete, locale: t("locale") });
        var alternateComponent = getComponentRenderer(props, type);
        return React.createElement(React.Fragment, null,
            !alternateComponent && renderDisplayText(),
            React.createElement(FieldWrap, null,
                alternateComponent || React.createElement(DateField, __assign({}, props)),
                !alternateComponent && isInvalid && React.createElement(Icon, { type: "error" })));
    };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * Display the time component.
     * If a custom renderer is provided in the context, use that
     *
     */
    var renderTime = function () {
        var props = __assign(__assign(__assign({}, inputProps), input), { dateFormat: "HH:mm", name: input.name, defaultValue: input.onChange ? undefined : input.defaultValue, locale: t("locale") });
        var alternateComponent = getComponentRenderer(props, type);
        return React.createElement(React.Fragment, null,
            !alternateComponent && renderDisplayText(),
            React.createElement(FieldWrap, null,
                alternateComponent || React.createElement(TimeField, __assign({}, props)),
                !alternateComponent && !isInvalid && React.createElement(Icon, { style: { cursor: "pointer" }, type: "clock" }),
                !alternateComponent && isInvalid && React.createElement(Icon, { type: "error" })));
    };
    /**
     *
     * @returns JSX.Element | JSX.element[]
     * The component to be displayed
     *
     */
    var render = function () {
        switch (type) {
            case 'radio':
            case 'checkbox':
                return renderCheckboxRadio();
            case 'date-picker':
            case 'date':
                return renderDatePicker();
            case "birthday":
                return renderBirthday();
            case 'time':
                return renderTime();
            case 'hidden':
                return renderInput({ style: { display: "none !important" } });
            default:
                return renderInput();
        }
        ;
    };
    /**
     *
     * Render an hidden field if the field has to be displayed in the UI.
     *
     */
    if (type === 'hidden')
        return render();
    var wrapperClassName = getClassName(className, INPUT_GROUP, name + INPUT_GROUP_POST_FIX, typeClassName + INPUT_GROUP_POST_FIX);
    var inputContainerProps = {
        forwardedAs: forwardedAs,
        isInline: displayInline,
        children: render(),
        className: getClassName(name + "-input", (_a = {},
            _a[IS_INVALID] = isInvalid,
            _a[FLEX_NOWRAP] = isRadioOrCheckbox,
            _a))
    };
    return React.createElement("div", { className: wrapperClassName },
        React.createElement(RecommendedField, { recommended: renderAsRecommended && recommended },
            renderDateFieldPortal(),
            React.createElement(Flex, __assign({}, inputContainerProps)),
            renderDescription()));
};
var RecommendedField = function (_a) {
    var recommended = _a.recommended, children = _a.children;
    var t = useTranslation().t, title = t("recommended_consent.title") || "", description = t("recommended_consent.description") || "", Recommended = usePresentationComponent("recommended.wrap") || RecommendedComponent, RecommendedTitle = usePresentationComponent("recommended.header") || RecommendedComponentTitle, RecommendedField = usePresentationComponent("recommended.field") || RecommendedComponentInner;
    if (!recommended)
        return React.createElement(React.Fragment, null, children);
    return (React.createElement(Recommended, { className: INPUT_RECOMMENDED },
        React.createElement(RecommendedTitle, { className: INPUT_RECOMMENDED_TITLE },
            title && parse(title),
            description && parse(description)),
        React.createElement(RecommendedField, { className: INPUT_RECOMMENDED_INNER }, children)));
};
export var Input = styled(InputNoStyle)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    .", " {\n        width: 100%;\n        > *:first-child { margin-top: 0; }\n        > *:last-child { margin-bottom: 0; }\n        * {\n            margin: 0;\n        }\n    }\n    .", " .", " {\n        appearance: none;\n        padding-right: 2.7em !important;\n    }\n\n    input[type=\"checkbox\"], input[type=\"radio\"] {\n        margin: 0;\n        margin-right: 5px;\n        flex-shrink: 0;\n    }\n"], ["\n    .", " {\n        width: 100%;\n        > *:first-child { margin-top: 0; }\n        > *:last-child { margin-bottom: 0; }\n        * {\n            margin: 0;\n        }\n    }\n    .", " .", " {\n        appearance: none;\n        padding-right: 2.7em !important;\n    }\n\n    input[type=\"checkbox\"], input[type=\"radio\"] {\n        margin: 0;\n        margin-right: 5px;\n        flex-shrink: 0;\n    }\n"])), INPUT_DESCRIPTION, IS_INVALID, INPUT);
var RecommendedComponent = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    color: var(--ws-recommended-input-text);\n    background: var(--ws-recommended-input-bg);\n    padding: var(--ws-recommended-input-padding);\n    border-radius: var(--ws-recommended-input-border-radius, 0);\n    border-width: var(--ws-recommended-input-border-width, 1px);\n    border-style: var(--ws-recommended-input-border-solid, solid);\n    border-color: var(--ws-recommended-input-border-color, transparent);\n\n    .", " {\n        padding-left: 0 !important;\n    }\n\n"], ["\n    color: var(--ws-recommended-input-text);\n    background: var(--ws-recommended-input-bg);\n    padding: var(--ws-recommended-input-padding);\n    border-radius: var(--ws-recommended-input-border-radius, 0);\n    border-width: var(--ws-recommended-input-border-width, 1px);\n    border-style: var(--ws-recommended-input-border-solid, solid);\n    border-color: var(--ws-recommended-input-border-color, transparent);\n\n    .", " {\n        padding-left: 0 !important;\n    }\n\n"])), INPUT_DESCRIPTION);
var RecommendedComponentInner = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n    position: relative;\n    padding-left: 2em;\n\n    input[type=\"checkbox\"] {\n        width: 1.5em;\n        height: 1.5em;\n        margin-right: 1em;\n        position: absolute;\n        top: 0;\n        left: 0;\n    }\n"], ["\n    position: relative;\n    padding-left: 2em;\n\n    input[type=\"checkbox\"] {\n        width: 1.5em;\n        height: 1.5em;\n        margin-right: 1em;\n        position: absolute;\n        top: 0;\n        left: 0;\n    }\n"])));
var RecommendedComponentTitle = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    color: var(--ws-recommended-title-text);\n    margin-bottom: 1em !important;\n\n    h1, h2, h3, h4, h5, h6, p {\n        margin-bottom: .5em;\n        &:first-child { margin-top: 0 !important; }\n        &:last-child { margin-bottom: 0 !important; }\n        color: inherit;\n    };\n"], ["\n    color: var(--ws-recommended-title-text);\n    margin-bottom: 1em !important;\n\n    h1, h2, h3, h4, h5, h6, p {\n        margin-bottom: .5em;\n        &:first-child { margin-top: 0 !important; }\n        &:last-child { margin-bottom: 0 !important; }\n        color: inherit;\n    };\n"])));
var FieldWrap = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    position: relative;\n    .react-datepicker-wrapper, .", " { width: 100%; }\n"], ["\n    position: relative;\n    .react-datepicker-wrapper, .", " { width: 100%; }\n"])), INPUT);
var Icon = styled(SvgIcon)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    position: absolute;\n    right: .6em;\n    height: calc(100% - .6em);\n    top: 50%;\n    transform: translateY(-50%);\n    ", "\n"], ["\n    position: absolute;\n    right: .6em;\n    height: calc(100% - .6em);\n    top: 50%;\n    transform: translateY(-50%);\n    ", "\n"])), function (_a) {
    var width = _a.width;
    return "width : " + (width || "1em") + ";";
});
var DatePortal = styled.div(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n    position: absolute;\n    top: 0;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    pointer-events: none;\n    z-index: 1000;\n    > * {\n        pointer-events: auto;\n    }\n"], ["\n    position: absolute;\n    top: 0;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    pointer-events: none;\n    z-index: 1000;\n    > * {\n        pointer-events: auto;\n    }\n"])));
export var ReactiveInput = function (_a) {
    var getDynamicValue = _a.getDynamicValue, props = __rest(_a, ["getDynamicValue"]);
    var _b = props.type, type = _b === void 0 ? 'text' : _b, className = props.className, name = props.name;
    var reactiveProps = useFieldApi(props);
    var formOptions = useFormApi();
    var reset = (reactiveProps === null || reactiveProps === void 0 ? void 0 : reactiveProps.reset) || null;
    if (getDynamicValue) {
        var onChange_1 = reactiveProps.input.onChange;
        reactiveProps.input.onChange = function (x) {
            var selectedValue = x;
            if (x === null || x === void 0 ? void 0 : x.currentTarget) {
                selectedValue = ["checkbox", "radio"].includes(x.currentTarget.type.toLowerCase()) ? x.currentTarget.checked : x.currentTarget.value;
            }
            var obj = getDynamicValue({ value: selectedValue }, formOptions.getState());
            var fields = [].concat(obj);
            formOptions.batch(function () {
                formOptions.change("skip", true);
                fields.forEach(function (_a) {
                    var key = _a.key, value = _a.value;
                    if (key) {
                        formOptions.change(key, value);
                    }
                    ;
                });
                formOptions.change("skip", undefined);
            });
            if ((x === null || x === void 0 ? void 0 : x.currentTarget) && !fields.some(function (field) { return get(field, "key") === name; })) {
                onChange_1(x);
            }
        };
    }
    return React.createElement(Input, __assign({ type: type, className: className, name: name }, reactiveProps));
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
